/* De offertepagina (/crm/quotes/:quoteId): één pagina in plaats van vijf tabs (ADR 0018).
 *
 *   kop            nummer, titel, status met signaal, bedrag, geldigheid, project, revisie
 *   sectiebalk     blijft boven staan; springt naar een sectie
 *   links          regels, tekst, voorwaarden, revisies (tijdlijn met bedragverloop), gegevens
 *   rechts         de PDF zoals hij nu is (ververst mee); op smalle schermen via "Volledig scherm"
 *
 * "Opslaan in projectmap" legt een revisie vast en zet haar PDF als Versie in de
 * projectmap (alleen met een project); zonder project blijft het een download. */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { useQuote } from '@/hooks/useQuote';
import { useQuotePDF } from '@/hooks/useQuotePDF';
import { supabase } from '@/integrations/supabase/client';
import type { QuoteVersion } from '@/types/quote';
import { QuoteLineItemsEditor } from '@/components/crm/quote/QuoteLineItemsEditor';
import { QuoteSplitPreview } from '@/components/crm/quote/QuoteSplitPreview';
import { QuotePreviewModal } from '@/components/crm/quote/QuotePreviewModal';
import { QuoteVersionModal } from '@/components/crm/quote/QuoteVersionModal';
import { Sectiebalk, type Sectie } from '../projectpagina/ui/Sectiebalk';
import {
  useBewaarInProjectmap, useDupliceer, useLegRevisieVast, useOfferteProject, useRevisies, useStatusActie, useVerwijder,
} from './hooks';
import { haalVolledigeRevisie, revisieKeuze, type Revisie } from './data/revisies';
import { lokaleDag } from './data/status';
import { lijstAdres } from './data/lijst';
import type { OfferteClient } from './data/acties';
import { OfferteKop, type OfferteMetStatus } from './ui/OfferteKop';
import { VerstuurdDialoog, BesluitDialoog } from './ui/StatusDialogen';
import { RevisieDialoog, type RevisieBevestiging } from './ui/RevisieDialoog';
import { RevisieTijdlijn } from './ui/RevisieTijdlijn';
import { TekstBlok } from './ui/TekstBlok';
import { VoorwaardenBlok } from './ui/VoorwaardenBlok';
import { GegevensBlok } from './ui/GegevensBlok';

const ADMIN = new Set(['admin', 'super_admin']);
const client = supabase as unknown as OfferteClient;

function Deel({ id, titel, aantal, children, rechts }: { id: string; titel: string; aantal?: number | null; children: React.ReactNode; rechts?: React.ReactNode }) {
  return (
    <section id={id} aria-labelledby={`offerte-${id}`} className="flex scroll-mt-14 flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <h2 id={`offerte-${id}`} className="text-lg font-semibold">
          {titel} {aantal != null && <span className="font-normal text-muted-foreground">{aantal}</span>}
        </h2>
        {rechts}
      </div>
      {children}
    </section>
  );
}

export function OffertePagina({ quoteId }: { quoteId: string }) {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isAdmin = ADMIN.has(userRole ?? '');
  const {
    quote: q, lineItems, loading, saveStatus, calculatedTotals, updateQuote, restoreVersion, refetch,
    addLineItem, updateLineItem, deleteLineItem, duplicateLineItem, reorderLineItems,
  } = useQuote(quoteId);
  const quote = q as OfferteMetStatus | null;
  const revisies = useRevisies(quoteId);
  const project = useOfferteProject(quote ? quote.project_id : undefined);
  const { exportPDF, previewPDF, isGenerating } = useQuotePDF();
  const status = useStatusActie(quote ? {
    id: quote.id, quote_number: quote.quote_number, title: quote.title, status: quote.status, current_version: quote.current_version,
    project_id: quote.project_id, charging_location_id: quote.charging_location_id, charging_opportunity_id: quote.charging_opportunity_id,
    sent_on: quote.sent_on ?? null, follow_up_todo_id: quote.follow_up_todo_id ?? null,
  } : null);
  const bewaar = useBewaarInProjectmap(quoteId);
  const vastleggen = useLegRevisieVast(quoteId);
  const dupliceer = useDupliceer();
  const verwijder = useVerwijder(quoteId);
  const kopRef = useRef<HTMLElement>(null);

  const [verstuurdOpen, setVerstuurdOpen] = useState(false);
  const [besluitOpen, setBesluitOpen] = useState(false);
  const [besluit, setBesluit] = useState<'accepted' | 'declined'>('accepted');
  const [revisieOpen, setRevisieOpen] = useState(false);
  // Los van open: tijdens het sluiten blijft de dialoog zoals hij was.
  const [revisieModus, setRevisieModus] = useState<'pdf' | 'revisie'>('pdf');
  const openRevisie = (m: 'pdf' | 'revisie') => {
    setRevisieModus(m);
    setRevisieOpen(true);
  };
  const [volledig, setVolledig] = useState(false);
  const [bekijk, setBekijk] = useState<QuoteVersion | null>(null);
  const [weg, setWeg] = useState(false);
  const [tekstSleutel, setTekstSleutel] = useState(0);
  const [metPdf, setMetPdf] = useState(() => {
    try {
      return localStorage.getItem('offerte:pdf-ernaast') !== 'nee';
    } catch {
      return true;
    }
  });
  const wisselPdf = () => {
    setMetPdf((v) => {
      try {
        localStorage.setItem('offerte:pdf-ernaast', v ? 'nee' : 'ja');
      } catch {
        /* geen opslag: dan geldt het alleen voor nu */
      }
      return !v;
    });
  };
  const vandaag = lokaleDag();

  const lijst = useMemo(() => revisies.data ?? [], [revisies.data]);
  const keuze = useMemo(
    () => revisieKeuze({ current_version: quote?.current_version ?? 1, updated_at: quote?.updated_at }, lijst, calculatedTotals.total),
    [quote?.current_version, quote?.updated_at, lijst, calculatedTotals.total],
  );
  const vorige = lijst.find((r) => r.file_name) ?? null;

  if (loading && !quote) {
    return (
      <div className="flex flex-col gap-4" aria-busy>
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-9 w-1/2 animate-pulse rounded bg-muted" />
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(340px,38%)]">
          <div className="h-96 animate-pulse rounded-xl bg-muted" />
          <div className="h-96 animate-pulse rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="py-16 text-center">
        <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" aria-hidden />
        <h1 className="mb-4 text-lg font-medium">Offerte niet gevonden</h1>
        <Button onClick={() => navigate('/crm/quotes')}>Terug naar offertes</Button>
      </div>
    );
  }

  const fout = (titel: string) => (e: unknown) => toast.error(titel, { description: (e as Error)?.message ?? String(e), duration: 12_000 });
  const opnieuwLaden = () => {
    refetch();
    revisies.refetch();
  };

  const contact = quote.contact;
  const standaardAan = contact
    ? `${contact.first_name} ${contact.last_name}`.trim() + (contact.email ? ` <${contact.email}>` : '')
    : quote.sent_to ?? '';

  const bevestigRevisie = (b: RevisieBevestiging) => {
    if (revisieModus === 'pdf') {
      if (!project.data) return;
      bewaar.mutate(
        { quote: { ...quote, updated_at: quote.updated_at }, project: project.data, doel: b.doel, omschrijving: b.omschrijving, vorige },
        {
          onSuccess: (uit) => {
            setRevisieOpen(false);
            toast.success(`Revisie ${uit.revisie.version_number} staat in de projectmap`, {
              description: uit.naam,
              action: uit.web_url ? { label: 'Openen', onClick: () => window.open(uit.web_url!, '_blank', 'noopener') } : undefined,
            });
          },
          onError: fout('Opslaan in projectmap mislukt'),
          onSettled: opnieuwLaden,
        },
      );
    } else if (b.doel.soort === 'nieuw') {
      vastleggen.mutate(
        { q: quote, nummer: b.doel.nummer, notitie: b.omschrijving || null },
        {
          onSuccess: (r) => {
            setRevisieOpen(false);
            toast.success(`Revisie ${r.version_number} vastgelegd`);
          },
          onError: fout('Revisie vastleggen mislukt'),
          onSettled: opnieuwLaden,
        },
      );
    }
  };

  const bekijkRevisie = async (r: Revisie) => {
    try {
      setBekijk((await haalVolledigeRevisie(client, r.id)) as QuoteVersion);
    } catch (e) {
      fout('Revisie laden mislukt')(e);
    }
  };

  const zetTerug = async (r: Revisie) => {
    try {
      await restoreVersion((await haalVolledigeRevisie(client, r.id)) as QuoteVersion);
      revisies.refetch();
    } catch (e) {
      fout('Terugzetten mislukt')(e);
    }
  };

  const secties: Sectie[] = [
    { id: 'regels', label: 'Regels', aantal: lineItems.length },
    { id: 'tekst', label: 'Tekst' },
    { id: 'voorwaarden', label: 'Voorwaarden' },
    { id: 'revisies', label: 'Revisies', aantal: revisies.data ? lijst.length : null },
    { id: 'gegevens', label: 'Gegevens' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <OfferteKop
        kopRef={kopRef}
        quote={quote}
        revisies={lijst}
        werkBedrag={calculatedTotals.total}
        bewaarstand={saveStatus}
        vandaag={vandaag}
        terugNaar={lijstAdres()}
        bezigPdf={bewaar.isPending || isGenerating}
        bezigStatus={status.isPending}
        pdfErnaast={metPdf}
        acties={{
          onWisselPdf: wisselPdf,
          onVerstuurd: () => setVerstuurdOpen(true),
          onBesluit: (u) => {
            setBesluit(u);
            setBesluitOpen(true);
          },
          onTerug: () => status.mutate({ soort: 'terug' }, { onSuccess: () => toast.success('Status teruggezet'), onError: fout('Terugzetten mislukt'), onSettled: refetch }),
          onProjectmap: () => {
            if (project.data && !project.data.sharepoint_folder_path) {
              toast.error('Dit project heeft nog geen projectmap', { description: 'Koppel eerst een SharePoint-map op de projectpagina.' });
              return;
            }
            openRevisie('pdf');
          },
          onDownload: () => exportPDF(quote.id, 'print'),
          onPdfTab: () => previewPDF(quote.id, true),
          onRevisie: () => openRevisie('revisie'),
          onVolledigScherm: () => setVolledig(true),
          onDupliceer: () => dupliceer.mutate(quote as any, {
            onSuccess: (id) => {
              toast.success('Offerte gedupliceerd');
              navigate(`/crm/quotes/${id}`);
            },
            onError: fout('Dupliceren mislukt'),
          }),
          onVerwijder: () => setWeg(true),
          onTitel: (t) => updateQuote({ title: t }, { silent: true, refetch: false }),
          onDatum: (veld, dag) => updateQuote({ [veld]: dag } as any, { silent: true, refetch: false }),
        }}
      />
      <Sectiebalk secties={secties} kop={kopRef} titel={`${quote.quote_number} · ${quote.title}`} label="Secties van deze offerte" />

      <div className={cn('grid items-start gap-6', metPdf && 'xl:grid-cols-[minmax(0,1fr)_minmax(340px,38%)]')}>
        <div className="flex min-w-0 flex-col gap-6">
          <section id="regels" aria-label="Regels" className="scroll-mt-14">
            <QuoteLineItemsEditor
              quoteId={quote.id}
              lineItems={lineItems}
              pricingMode={quote.pricing_mode || 'inclusive'}
              addLineItem={addLineItem}
              updateLineItem={updateLineItem}
              deleteLineItem={deleteLineItem}
              duplicateLineItem={duplicateLineItem}
              reorderLineItems={reorderLineItems}
            />
          </section>
          <Deel id="tekst" titel="Tekst">
            <TekstBlok key={`${quote.id}-${tekstSleutel}`} quote={quote} onUpdate={updateQuote} />
          </Deel>
          <Deel id="voorwaarden" titel="Voorwaarden">
            <VoorwaardenBlok quote={quote} onUpdate={updateQuote} />
          </Deel>
          <Deel
            id="revisies"
            titel="Revisies"
            aantal={revisies.data ? lijst.length : null}
            rechts={
              <Button variant="outline" size="sm" className="h-8" onClick={() => openRevisie(quote.project_id ? 'pdf' : 'revisie')}>
                {quote.project_id ? 'Opslaan in projectmap' : 'Revisie vastleggen'}
              </Button>
            }
          >
            <RevisieTijdlijn
              revisies={lijst}
              laden={revisies.isPending}
              huidig={quote.current_version ?? 1}
              werkBedrag={calculatedTotals.total}
              verstuurd={{ revisie: quote.sent_revision ?? null, op: quote.sent_on ?? null, aan: quote.sent_to ?? null }}
              onBekijk={bekijkRevisie}
              onTerugzetten={zetTerug}
              bezig={saveStatus === 'saving'}
            />
          </Deel>
          <Deel id="gegevens" titel="Gegevens">
            <GegevensBlok quote={quote} onUpdate={updateQuote} onTekstenVervangen={() => setTekstSleutel((n) => n + 1)} />
          </Deel>
        </div>

        {metPdf && (
          <aside aria-label="PDF" className="sticky top-14 hidden min-w-0 xl:block">
            <QuoteSplitPreview quote={quote} lineItems={lineItems} className="h-[calc(100vh-8.5rem)]" />
          </aside>
        )}
      </div>

      <QuotePreviewModal open={volledig} onOpenChange={setVolledig} quote={quote} lineItems={lineItems} />

      <VerstuurdDialoog
        open={verstuurdOpen}
        onOpenChange={setVerstuurdOpen}
        standaardAan={standaardAan}
        revisie={quote.current_version ?? null}
        opnieuw={quote.status === 'sent'}
        waarschuwing={
          quote.project_id && !lijst.some((r) => r.version_number === (quote.current_version ?? 1) && r.file_name)
            ? `Revisie ${quote.current_version ?? 1} staat nog niet als PDF in de projectmap. Sla hem eerst op, dan is later precies terug te zien wat de klant kreeg.`
            : null
        }
        bezig={status.isPending}
        onBevestig={(v) =>
          status.mutate(
            { soort: 'verstuurd', v: { ...v, revisie: quote.current_version ?? null } },
            {
              onSuccess: () => {
                setVerstuurdOpen(false);
                toast.success('Gemarkeerd als verstuurd', { description: 'Herinnertaak over 7 dagen staat in je taken.' });
              },
              onError: fout('Markeren als verstuurd mislukt'),
              onSettled: refetch,
            },
          )
        }
      />
      <BesluitDialoog
        open={besluitOpen}
        uitkomst={besluit}
        onOpenChange={setBesluitOpen}
        bezig={status.isPending}
        onBevestig={(b) =>
          status.mutate(
            { soort: 'besluit', b: { uitkomst: besluit, op: b.op, notitie: b.notitie } },
            {
              onSuccess: () => {
                toast.success(besluit === 'accepted' ? 'Geaccepteerd vastgelegd' : 'Afgewezen vastgelegd');
                setBesluitOpen(false);
              },
              onError: fout('Besluit vastleggen mislukt'),
              onSettled: refetch,
            },
          )
        }
      />
      <RevisieDialoog
        open={revisieOpen}
        onOpenChange={setRevisieOpen}
        metPdf={revisieModus === 'pdf'}
        keuze={keuze}
        quote={{ quote_number: quote.quote_number, current_version: quote.current_version }}
        project={project.data ?? null}
        vorige={vorige}
        magNieuweVersie={isAdmin}
        bezig={bewaar.isPending || vastleggen.isPending}
        onBevestig={bevestigRevisie}
      />
      {bekijk && (
        <QuoteVersionModal
          version={bekijk}
          open
          onOpenChange={(o) => !o && setBekijk(null)}
          onRestore={async () => {
            const v = bekijk;
            setBekijk(null);
            await restoreVersion(v);
            revisies.refetch();
          }}
        />
      )}
      <AlertDialog open={weg} onOpenChange={setWeg}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Offerte verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              {quote.quote_number} verdwijnt uit de lijst. Revisies en bestanden in de projectmap blijven staan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => verwijder.mutate(undefined, {
                onSuccess: () => {
                  toast.success('Offerte verwijderd');
                  navigate(lijstAdres());
                },
                onError: fout('Verwijderen mislukt'),
              })}
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
